import React, { useCallback, useEffect, useState } from 'react'
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CProgress,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilPeople, cilReload, cilSwapHorizontal, cilWarning } from '@coreui/icons'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import { InfoBox } from 'src/components/common/ImportWizard'
import './UserTrade.css'

// The userTrade endpoint only filters on full_name.
const SEARCH_FIELDS = [{ key: 'full_name', label: 'Member' }]

const columns = [
  { key: 'full_name', label: 'Member', filter: false },
  { key: 'totalTrades', label: 'Trades Used', filter: false, _style: { width: '35%' } },
  { key: 'trade_left', label: 'Trades Left', filter: false, _style: { width: '15%' } },
]

// Under a quarter of the allowance left is worth flagging before it runs out.
const tradesLeftColor = (left, allowance) => {
  if (left <= 0) return 'danger'
  if (allowance > 0 && left / allowance <= 0.25) return 'warning'
  return 'success'
}

const UserTrade = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [searchValue, setSearchValue] = useState('')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [users, setUsers] = useState([])

  const fetchUsers = useCallback(() => {
    setLoading(true)
    setError(false)
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    searchValue.trim() && params.append(searchField, searchValue.trim())
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    PlayerService.userTrade(offset, itemsPerPage, activePage, params)
      .then((result) => {
        setUsers(result.data)
      })
      .catch(() => {
        setError(true)
        ToastComponent('Could not load trade usage', 'error')
      })
      .finally(() => setLoading(false))
  }, [activePage, searchField, searchValue, columnSorter, itemsPerPage])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // Every row carries the game's allowance, so any row tells us what it is.
  const allowance = users?.data?.[0]?.trades

  const STAT_CARDS = [
    {
      label: 'Members Trading',
      value: users?.total ?? 0,
      icon: cilPeople,
      color: 'primary',
    },
    {
      label: 'Trades Per Member',
      value: allowance ?? '—',
      icon: cilSwapHorizontal,
      color: 'info',
    },
  ]

  const noItemsLabel = searchValue.trim()
    ? 'No members match your search'
    : 'No members have a saved team yet'

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Trades Used</h1>
        <p className="ge-page-subtitle">
          How many trades each member has made this season and how many they have left.
        </p>
      </div>

      <CRow className="g-3 mb-3">
        {STAT_CARDS.map((s) => (
          <CCol sm={6} key={s.label}>
            <CCard className="h-100">
              <CCardBody className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-medium-emphasis small">{s.label}</div>
                  <div className="fs-4 fw-semibold">{s.value}</div>
                </div>
                <div className={`ut-stat-icon ut-stat-icon--${s.color}`}>
                  <CIcon icon={s.icon} size="lg" />
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        ))}
      </CRow>

      <CCard>
        <CCardBody>
          <InfoBox>
            Trades left is the game&apos;s trade allowance minus the trades a member has made. Only
            members with a saved team are listed.
          </InfoBox>

          {error ? (
            <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2 mb-0">
              <CIcon icon={cilWarning} className="flex-shrink-0" />
              <span>We couldn&apos;t load trade usage.</span>
              <CButton
                color="danger"
                variant="outline"
                size="sm"
                className="ms-auto"
                onClick={fetchUsers}
              >
                <CIcon icon={cilReload} className="me-1" />
                Retry
              </CButton>
            </CAlert>
          ) : (
            <>
              <TableSearchToolbar
                fields={SEARCH_FIELDS}
                field={searchField}
                onFieldChange={(field) => {
                  setSearchField(field)
                  setSearchValue('')
                  setActivePage(1)
                }}
                value={searchValue}
                onSearch={(value) => {
                  setActivePage(1)
                  setSearchValue(value)
                }}
                placeholder="Search members..."
              />

              <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
                <CSmartTable
                  columns={columns}
                  columnSorter={{ external: true }}
                  scopedColumns={{
                    full_name: (item) => (
                      <td>
                        <div className="ut-member-name">{item.full_name || 'Unnamed member'}</div>
                        {item.email && <div className="ut-member-meta">{item.email}</div>}
                      </td>
                    ),
                    totalTrades: (item) => {
                      const used = Number(item.totalTrades) || 0
                      const total = Number(item.trades) || 0
                      return (
                        <td>
                          <div className="ut-usage">
                            <span className="ut-usage-count">
                              {used}
                              {total > 0 && <span className="ut-usage-total"> / {total}</span>}
                            </span>
                            {total > 0 && (
                              <CProgress
                                thin
                                className="ut-usage-bar"
                                value={Math.min(100, (used / total) * 100)}
                                color={tradesLeftColor(total - used, total)}
                              />
                            )}
                          </div>
                        </td>
                      )
                    },
                    trade_left: (item) => {
                      const left = Number(item.trade_left) || 0
                      return (
                        <td>
                          <CBadge
                            shape="rounded-pill"
                            color={tradesLeftColor(left, Number(item.trades) || 0)}
                          >
                            {left} left
                          </CBadge>
                        </td>
                      )
                    },
                  }}
                  items={users?.data}
                  itemsPerPage={itemsPerPage}
                  itemsPerPageSelect
                  loading={loading}
                  noItemsLabel={noItemsLabel}
                  pagination={{ external: true }}
                  paginationProps={{
                    activePage: activePage,
                    pages: Math.ceil(users?.total / itemsPerPage) || 1,
                  }}
                  tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                  onActivePageChange={(activePage) => setActivePage(activePage)}
                  onItemsPerPageChange={(itemsPerPage) => {
                    setActivePage(1)
                    setItemsPerPage(itemsPerPage)
                  }}
                  onSorterChange={(sorter) => setColumnSorter(sorter)}
                />
              </div>
            </>
          )}
        </CCardBody>
      </CCard>
    </>
  )
}

export default UserTrade
